import { useEffect, useRef } from 'react';

interface GlowingTextProps {
    text: string;
    className?: string;
}

const GlowingText = ({ text, className = '' }: GlowingTextProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const cursorRef = useRef({ x: 0, y: 0 });

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleMouseMove = (e: MouseEvent) => {
            if (!container) return;
            const rect = container.getBoundingClientRect();
            cursorRef.current = {
                x: e.clientX - rect.left,
                y: e.clientY - rect.top
            };

            // Update CSS custom properties for glow effect
            container.style.setProperty('--mouse-x', `${cursorRef.current.x}px`);
            container.style.setProperty('--mouse-y', `${cursorRef.current.y}px`);
        };

        container.addEventListener('mousemove', handleMouseMove);

        return () => {
            container.removeEventListener('mousemove', handleMouseMove);
        };
    }, []);

    return (
        <div 
            ref={containerRef} 
            className={`glowing-text-container relative ${className}`}
            style={{
                '--mouse-x': '50%',
                '--mouse-y': '50%',
            } as React.CSSProperties}
        >
            {/* Main text */}
            <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl xl:text-8xl font-playfair font-black text-romance-ink text-center leading-tight tracking-tight relative z-10 px-4">
                {text}
            </h1>
            
            {/* Glowing duplicate layers for depth */}
            <h1 
                className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl xl:text-8xl font-playfair font-black text-center leading-tight tracking-tight absolute top-0 left-0 w-full pointer-events-none px-4"
                style={{
                    background: 'radial-gradient(circle at var(--mouse-x) var(--mouse-y), rgba(255, 182, 193, 0.6), transparent 40%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    filter: 'blur(8px)',
                    opacity: 0.7,
                    transition: 'all 0.3s ease'
                }}
            >
                {text}
            </h1>
            
            {/* Secondary glow layer */}
            <h1 
                className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl xl:text-8xl font-playfair font-black text-center leading-tight tracking-tight absolute top-0 left-0 w-full pointer-events-none px-4"
                style={{
                    background: 'radial-gradient(circle at var(--mouse-x) var(--mouse-y), rgba(212, 175, 55, 0.5), transparent 30%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    filter: 'blur(15px)',
                    opacity: 0.5,
                    transition: 'all 0.3s ease'
                }}
            >
                {text}
            </h1>
        </div>
    );
};

export default GlowingText;
